import { useState } from "react";
import type { DocsBlock } from "@shared/api";
import { useDocs } from "@client/api/queries";

function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1_200);
    } catch {
      // clipboard no disponible
    }
  }

  return (
    <div className="relative overflow-hidden rounded-xl border border-base-300/70 bg-base-200/70">
      <div className="flex items-center justify-between border-b border-base-300/60 px-3 py-1.5">
        <span className="font-mono text-[10px] tracking-widest text-base-content/40 uppercase">
          {language}
        </span>
        <button
          type="button"
          onClick={copy}
          className="btn btn-ghost btn-xs transition-transform duration-200 active:scale-95"
        >
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-3 text-xs leading-relaxed">
        <code className="font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

function Block({ block }: { block: DocsBlock }) {
  switch (block.kind) {
    case "paragraph":
      return <p className="text-sm leading-relaxed text-base-content/80">{block.text}</p>;
    case "list":
      return (
        <ul className="flex flex-col gap-1.5">
          {block.items.map((item) => (
            <li key={item} className="flex gap-2 text-sm text-base-content/80">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "steps":
      return (
        <ol className="flex flex-col gap-1.5">
          {block.items.map((item, index) => (
            <li key={item} className="flex gap-2.5 text-sm text-base-content/80">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-bold text-primary">
                {index + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );
    case "code":
      return <CodeBlock code={block.code} language={block.language} />;
  }
}

// Visor de documentación: overlay que solo se pide/renderiza autenticado.
export function DocsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const docs = useDocs(open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center p-0 sm:items-center sm:p-6">
      <div
        className="absolute inset-0 animate-fade-in bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 flex h-full w-full max-w-3xl animate-bounce-in flex-col overflow-hidden border border-base-300 bg-base-100 shadow-2xl sm:h-[85vh] sm:rounded-2xl">
        <header className="flex items-center justify-between gap-3 border-b border-base-300/70 px-5 py-3.5">
          <div>
            <h2 className="text-lg font-bold">{docs.data?.title ?? "Documentación"}</h2>
            {docs.data?.updatedAt ? (
              <p className="text-xs text-base-content/40">Actualizada: {docs.data.updatedAt}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="btn btn-ghost btn-sm transition-transform duration-200 active:scale-95"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {docs.isLoading ? (
            <div className="flex flex-col gap-3">
              <div className="skeleton-shimmer h-5 w-2/3 rounded" />
              <div className="skeleton-shimmer h-24 rounded-xl" />
              <div className="skeleton-shimmer h-5 w-1/2 rounded" />
              <div className="skeleton-shimmer h-16 rounded-xl" />
            </div>
          ) : null}

          {docs.isError ? (
            <div role="alert" className="alert alert-error animate-shake text-sm">
              <span>No se pudo cargar la documentación. Vuelve a iniciar sesión.</span>
            </div>
          ) : null}

          {docs.data ? (
            <div className="flex flex-col gap-8">
              <p className="text-sm leading-relaxed text-base-content/70">{docs.data.intro}</p>
              {docs.data.sections.map((section) => (
                <section key={section.id} id={section.id} className="flex flex-col gap-3">
                  <h3 className="text-base font-semibold text-primary">{section.title}</h3>
                  {/* Contenido estático: el orden nunca cambia, el índice es estable. */}
                  {section.blocks.map((block, index) => (
                    // oxlint-disable-next-line react/no-array-index-key
                    <Block key={`${section.id}:${index}`} block={block} />
                  ))}
                </section>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
